/**
 * AuditPage — domain form + scanning animation.
 * Once complete, parent (App.tsx) saves the report and navigates to the
 * full AuditReportDetail page.
 */

import { useState, useEffect, type FormEvent } from 'react';
import type { AuditResponse } from '../types';

// ── Scanning animation ─────────────────────────────────────────────────────

const SCAN_STEPS = [
  'Fetching page and robots.txt…',
  'Parsing JSON-LD schema…',
  'Detecting FAQ content…',
  'Checking freshness signals…',
  'Computing AEO score…',
];

function AuditScanning({ domain }: { domain: string }) {
  return (
    <div className="flex flex-col items-center justify-center flex-1 px-4 text-center py-20">
      <div className="relative flex items-center justify-center mb-8">
        <span className="absolute inline-flex h-16 w-16 rounded-full bg-amber-400 opacity-20 animate-ping" />
        <span className="relative inline-flex h-10 w-10 rounded-full bg-amber-400" />
      </div>
      <h2 className="text-2xl font-medium text-brand-paper mb-2">Scanning {domain}</h2>
      <p className="text-brand-paper/50 text-sm mb-8 max-w-sm">
        Running four AI visibility checks in parallel — this takes about 15–30 seconds.
      </p>
      <ul className="text-left space-y-3 w-full max-w-sm">
        {SCAN_STEPS.map((step) => (
          <li key={step} className="flex items-center gap-3 text-sm">
            <span className="w-2 h-2 rounded-full bg-amber-400/40 shrink-0 animate-pulse" />
            <span className="text-brand-paper/50">{step}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Main export ────────────────────────────────────────────────────────────

interface AuditPageProps {
  /** Called when the audit API responds. App.tsx saves + navigates to report. */
  onComplete: (data: AuditResponse) => void;
}

export function AuditPage({ onComplete }: AuditPageProps) {
  const urlDomain = new URLSearchParams(window.location.search).get('domain') ?? '';
  const [scanning, setScanning] = useState(false);
  const [scanDomain, setScanDomain] = useState('');
  const [domain, setDomain] = useState(urlDomain);
  const [error, setError] = useState('');

  // Auto-start the scan when a domain is supplied via the URL ?domain= param
  useEffect(() => {
    if (!urlDomain) return;
    const trimmed = urlDomain.trim().replace(/^https?:\/\//i, '').replace(/\/$/, '');
    if (!trimmed || trimmed.length < 3) return;
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(trimmed)) return;
    runAudit(trimmed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAudit = async (trimmed: string) => {
    setScanning(true);
    setScanDomain(trimmed);
    try {
      const resp = await fetch('https://api.fixmyseo.io/audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: trimmed }),
      });
      if (!resp.ok) {
        const err = (await resp.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${resp.status}`);
      }
      const data = (await resp.json()) as AuditResponse;
      onComplete(data);
    } catch (err) {
      setScanning(false);
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmed = domain.trim().replace(/^https?:\/\//i, '').replace(/\/$/, '');
    if (!trimmed || trimmed.length < 3) { setError('Please enter a valid domain name.'); return; }
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(trimmed)) { setError('Please enter a domain without http:// — e.g. yourbusiness.com'); return; }

    await runAudit(trimmed);
  };

  if (scanning) {
    return (
      <div className="min-h-screen bg-brand-ink flex flex-col">
        <AuditScanning domain={scanDomain} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-ink flex flex-col">
      <div className="flex flex-col items-center justify-center flex-1 px-6 py-12">
        <div className="w-full max-w-lg text-center">
          <h1 className="text-2xl font-medium text-brand-paper mb-2">Run an AI visibility audit</h1>
          <p className="text-brand-paper/40 text-sm mb-8">
            Check how visible your site is to ChatGPT, Perplexity, and Google AI Overviews.
            The full report is saved to your account.
          </p>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3" noValidate>
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="yourbusiness.com"
              aria-label="Your business domain"
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-brand-paper placeholder-brand-paper/30 text-base focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green transition-colors"
              autoComplete="url"
              spellCheck={false}
            />
            <button
              type="submit"
              className="bg-brand-green hover:bg-brand-green-dark text-brand-paper font-medium px-6 py-3.5 rounded-xl text-base transition-colors whitespace-nowrap"
            >
              Audit my site
            </button>
          </form>
          {error && <p className="text-red-400 text-sm text-left mt-2">{error}</p>}
        </div>
      </div>
    </div>
  );
}

